import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Alert
} from '@mui/material';
import {
  EmojiEvents,
  Star,
  Whatshot,
  Visibility,
  ThumbUp,
  Share,
  MovieFilter,
  TrendingUp,
  Speed,
  LocalMovies,
  Group,
  Timeline,
  Psychology,
  Diamond,
  Shield
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { achievementService } from '../../services/achievementService';

// Icon mapping for different categories
const getCategoryIcon = (category) => {
  switch (category) {
    case 'SOCIAL': return Group;
    case 'DISCOVERY': return Speed;
    case 'ENGAGEMENT': return MovieFilter;
    case 'MILESTONE': return EmojiEvents;
    case 'STREAK': return Whatshot;
    case 'COLLECTION': return LocalMovies;
    case 'GENRE': return Psychology;
    case 'FRANCHISE': return Shield;
    case 'CONTENT': return Star;
    case 'EXPERTISE': return Diamond;
    default: return EmojiEvents;
  }
};

const RARITY_COLORS = {
  common: '#9E9E9E',
  uncommon: '#4CAF50',
  rare: '#2196F3',
  epic: '#9C27B0',
  legendary: '#FF9800'
};

// Cache for achievement definitions to prevent duplicate API calls
let definitionsCache = null;
let definitionsCacheTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const AchievementSystem = ({ userId, showInProfile = false }) => {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState([]);
  const [achievementDefinitions, setAchievementDefinitions] = useState({});
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadingRef = React.useRef(false);

  useEffect(() => {
    const targetUserId = userId || user?.id;
    if (targetUserId && !loadingRef.current) {
      loadingRef.current = true;
      loadData().finally(() => {
        loadingRef.current = false;
      });
    }
  }, [userId, user?.id]);

  const loadData = async () => {
    console.log('🚀 loadData called');
    setLoading(true);
    try {
      await Promise.all([
        loadAchievementDefinitions(),
        loadUserAchievements()
      ]);
      console.log('✅ loadData completed');
    } catch (error) {
      console.error('❌ loadData error:', error);
    }
    setLoading(false);
  };

  const loadAchievementDefinitions = async () => {
    try {
      console.log('🔍 Loading achievement definitions...');
      // Check cache first
      const now = Date.now();
      if (definitionsCache && (now - definitionsCacheTime) < CACHE_DURATION) {
        console.log('✅ Using cached definitions:', Object.keys(definitionsCache));
        setAchievementDefinitions(definitionsCache);
        return;
      }
      console.log('📡 Fetching fresh definitions from API...');

      const { adminAchievementService } = await import('../../services/adminAchievementService');
      console.log('📡 Calling getAllAchievements...');
      const definitions = await adminAchievementService.getAllAchievements();
      console.log(`📊 Received ${definitions.length} definitions:`, definitions.map(d => d.name));
      const definitionsMap = {};
      
      definitions.forEach(def => {
        const achievementKey = def.name;
        console.log(`🔑 Mapping definition: ${achievementKey}`);
        definitionsMap[achievementKey] = {
          ...def,
          icon: getCategoryIcon(def.category),
          rarity: def.rarity || 'common',
          points: def.points || 10
        };
      });
      
      console.log('✅ Final definitions map keys:', Object.keys(definitionsMap));
      
      // Update cache
      definitionsCache = definitionsMap;
      definitionsCacheTime = now;
      
      setAchievementDefinitions(definitionsMap);
      console.log('✅ Definitions set in state');
    } catch (error) {
      console.error('Error loading achievement definitions:', error);
      setAchievementDefinitions({});
    }
  };

  const loadUserAchievements = async () => {
    try {
      const targetUserId = userId || user?.id;
      if (!targetUserId) return;
      
      const userAchievements = await achievementService.getUserAchievements(targetUserId);
      setAchievements(userAchievements);
    } catch (error) {
      console.error('Error loading achievements:', error);
    }
  };

  const renderAchievementCard = (achievement) => {
    const achievementType = achievement.achievementType || achievement.type;
    let definition = achievementDefinitions[achievementType];
    
    // If no definition found, log error and skip rendering
    if (!definition) {
      console.error(`Achievement definition not found for type: ${achievementType}`);
      console.log('Available definitions:', Object.keys(achievementDefinitions));
      console.log('Achievement data:', achievement);
      return null;
    }
    const IconComponent = definition.icon;
    const rarityColor = RARITY_COLORS[definition.rarity];

    return (
      <Card
        key={achievement.id}
        sx={{
          cursor: 'pointer',
          transition: 'transform 0.2s',
          '&:hover': { transform: 'scale(1.02)' },
          border: `2px solid ${rarityColor}`,
          background: `linear-gradient(135deg, ${rarityColor}15, transparent)`
        }}
        onClick={() => setSelectedAchievement(achievement)}
      >
        <CardContent sx={{ textAlign: 'center', p: 2 }}>
          <Avatar
            sx={{
              bgcolor: rarityColor,
              width: 48,
              height: 48,
              mx: 'auto',
              mb: 1
            }}
          >
            <IconComponent />
          </Avatar>
          <Typography variant="h6" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
            {definition.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {definition.description}
          </Typography>
          <Chip
            label={definition.rarity}
            size="small"
            sx={{
              bgcolor: rarityColor,
              color: 'white',
              fontSize: '0.7rem',
              textTransform: 'capitalize'
            }}
          />
        </CardContent>
      </Card>
    );
  };

  const unlockedAchievements = achievements.filter(a => a.unlocked);
  const totalPoints = unlockedAchievements.reduce((sum, a) => {
    const achievementType = a.achievementType || a.type;
    const definition = achievementDefinitions[achievementType];
    return sum + (definition?.points || 10);
  }, 0);

  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.isAdmin || user?.groups?.includes('admin');

  const hasDefinitions = Object.keys(achievementDefinitions).length > 0;
  const hasAchievements = achievements.length > 0;
  
  console.log('🔍 Render check - Definitions:', hasDefinitions, 'Achievements:', hasAchievements, 'Loading:', loading, 'Unlocked:', unlockedAchievements.length);

  if (loading || !hasDefinitions) {
    return (
      <Box textAlign="center" py={4}>
        <Typography>Loading achievements...</Typography>
      </Box>
    );
  }

  // If no achievements, show empty state
  if (unlockedAchievements.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" gutterBottom>🎯 No Achievements Yet</Typography>
        <Typography color="text.secondary">Start exploring to unlock achievements!</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* User can edit their achievement display preferences */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box />
        <Button 
          variant="text" 
          size="small"
          onClick={() => {
            // Toggle between showing all achievements or just recent ones
            // This could be expanded to include sorting options
            console.log('Achievement display options');
          }}
        >
          Display Options
        </Button>
      </Box>
      
      {isAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          🔧 Admin: You can manage achievements in the admin panel
        </Alert>
      )}
      
      <Box mb={3} textAlign="center">
        <Typography variant="h5" gutterBottom>
          🏆 Achievements
        </Typography>
        <Box display="flex" justifyContent="center" gap={2} mb={2}>
          <Chip
            icon={<EmojiEvents />}
            label={`${unlockedAchievements.length} Unlocked`}
            color="primary"
          />
          <Chip
            icon={<Star />}
            label={`${totalPoints} Points`}
            color="secondary"
          />
        </Box>
      </Box>

      <Grid container spacing={2}>
        {unlockedAchievements.map(renderAchievementCard)}
      </Grid>

      <Dialog
        open={!!selectedAchievement}
        onClose={() => setSelectedAchievement(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedAchievement && (() => {
          const achievementType = selectedAchievement.achievementType || selectedAchievement.type;
          const definition = achievementDefinitions[achievementType];
          if (!definition) {
            console.error('No definition found for dialog:', achievementType);
            return false;
          }
          return definition;
        })() && (
          <>
            <DialogTitle textAlign="center">
              {(() => {
                const achievementType = selectedAchievement.achievementType || selectedAchievement.type;
                const definition = achievementDefinitions[achievementType];
                return definition?.name || selectedAchievement.achievementName || achievementType;
              })()}
            </DialogTitle>
            <DialogContent>
              <Box textAlign="center" mb={2}>
                <Avatar
                  sx={{
                    bgcolor: (() => {
                      const achievementType = selectedAchievement.achievementType || selectedAchievement.type;
                      const definition = achievementDefinitions[achievementType];
                      return RARITY_COLORS[definition?.rarity || 'common'];
                    })(),
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  {(() => {
                    const achievementType = selectedAchievement.achievementType || selectedAchievement.type;
                    const definition = achievementDefinitions[achievementType];
                    const IconComponent = definition?.icon || EmojiEvents;
                    return React.createElement(IconComponent);
                  })()}
                </Avatar>
                <Typography variant="body1" gutterBottom>
                  {(() => {
                    const achievementType = selectedAchievement.achievementType || selectedAchievement.type;
                    const definition = achievementDefinitions[achievementType];
                    return definition?.description || 'Achievement unlocked';
                  })()}
                </Typography>
                <Chip
                  label={`${(() => {
                    const achievementType = selectedAchievement.achievementType || selectedAchievement.type;
                    const definition = achievementDefinitions[achievementType];
                    return definition?.points || 10;
                  })()} Points`}
                  color="primary"
                  sx={{ mb: 2 }}
                />
              </Box>
              <Box display="flex" gap={1} justifyContent="center">
                <Button
                  variant="contained"
                  startIcon={<Share />}
                  onClick={async () => {
                    try {
                      await achievementService.shareAchievement(user?.id, selectedAchievement.id, true);
                      
                      // Show success notification
                      const event = new CustomEvent('achievementShared', { 
                        detail: { 
                          message: 'Achievement shared to your feed!',
                          achievementName: selectedAchievement.achievementName || selectedAchievement.achievementType
                        } 
                      });
                      window.dispatchEvent(event);
                      
                      setSelectedAchievement(null);
                    } catch (error) {
                      console.error('Error sharing achievement:', error);
                      
                      // Show error notification
                      const event = new CustomEvent('achievementShareError', { 
                        detail: { message: 'Failed to share achievement. Please try again.' } 
                      });
                      window.dispatchEvent(event);
                    }
                  }}
                >
                  Share to Feed
                </Button>
                <Button 
                  variant="outlined"
                  onClick={() => {
                    // Copy achievement details to clipboard
                    const achievementType = selectedAchievement.achievementType || selectedAchievement.type;
                    const definition = achievementDefinitions[achievementType];
                    const text = `🏆 I just unlocked the "${definition?.name || achievementType}" achievement in HypeCircle! ${definition?.description || ''}`;
                    
                    navigator.clipboard.writeText(text).then(() => {
                      const event = new CustomEvent('achievementCopied', { 
                        detail: { message: 'Achievement copied to clipboard!' } 
                      });
                      window.dispatchEvent(event);
                    });
                  }}
                >
                  Copy Link
                </Button>
                <Button onClick={() => setSelectedAchievement(null)}>
                  Close
                </Button>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AchievementSystem;